import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Instagram, Facebook, Linkedin, MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "./ui/button";

// Alluz Logo Component
const AlluzLogo = ({ className = "", size = 60 }) => (
  <img 
    src="/logo-alluz.png" 
    alt="Alluz Energia" 
    className={className}
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

export default function CTAFooter() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com/alluzenergia", label: "Instagram" },
    { icon: Facebook, href: "https://facebook.com/alluzenergia", label: "Facebook" },
    { icon: Linkedin, href: "https://linkedin.com/company/alluzenergia", label: "LinkedIn" },
    { icon: MessageCircle, href: "https://wa.me/5544988574869", label: "WhatsApp" },
  ];

  return (
    <footer id="contato" className="relative" data-testid="footer-section">
      {/* CTA Section */}
      <section
        ref={ref}
        className="py-24 sm:py-32 px-4 sm:px-6 bg-gradient-to-b from-[#FFFBEB] via-[#FEF3C7] to-[#FDE68A] relative overflow-hidden"
      >
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-gradient-to-b from-[#FFB414]/20 to-transparent blur-[100px]" />
        </div>
        
        {/* Floating Logos */}
        <motion.div
          className="absolute top-20 left-10 opacity-10 hidden sm:block"
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        >
          <AlluzLogo size={80} />
        </motion.div>
        <motion.div
          className="absolute bottom-20 right-10 opacity-10 hidden sm:block"
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        >
          <AlluzLogo size={100} />
        </motion.div>
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative rounded-[2rem] sm:rounded-[3rem] overflow-hidden"
            data-testid="cta-box"
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB414] via-[#FF8C00] to-[#F59E0B]" />
            
            {/* Animated Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div 
                className="absolute inset-0"
                style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: "30px 30px",
                }}
              />
            </div>
            
            {/* Glowing Orbs */}
            <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-white/20 blur-[80px]" />
            <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-white/15 blur-[60px]" />
            
            {/* Content */}
            <div className="relative z-10 p-8 sm:p-12 lg:p-16 text-center">
              {/* Logo */}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="mb-6"
              >
                <AlluzLogo size={80} className="mx-auto drop-shadow-2xl" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-2xl sm:text-3xl lg:text-5xl font-black font-['Outfit'] text-white mb-5"
              >
                Pronto para{" "}
                <span className="text-[#92400E]">iluminar</span>
                <br />
                o seu futuro?
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-white/90 text-base sm:text-lg lg:text-xl mb-10 max-w-2xl mx-auto"
              >
                Parcele seu projeto em até{" "}
                <span className="text-[#92400E] font-black text-xl sm:text-2xl">84x</span> e comece a 
                economizar desde o primeiro mês.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <motion.a
                  href="https://simulador.alluzenergia.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    className="bg-white hover:bg-white/90 text-[#FF8C00] font-bold text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-7 rounded-2xl flex items-center gap-3 shadow-xl"
                    data-testid="footer-simular-btn"
                  >
                    <AlluzLogo size={28} className="!filter-none !animate-none" />
                    Simular Sistema Agora
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </motion.a>
                
                <motion.a
                  href="https://wa.me/5544988574869"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    className="border-2 border-white/50 bg-white/10 backdrop-blur-sm text-white font-bold text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 rounded-2xl flex items-center gap-3 hover:bg-white/20 hover:border-white"
                    data-testid="footer-contato-btn"
                  >
                    <Phone className="w-5 h-5" />
                    Fale Conosco
                  </Button>
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <div className="bg-gradient-to-b from-[#92400E] to-[#78350F] py-14 sm:py-16 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12 mb-10">
            {/* Logo & Description */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-5">
                <AlluzLogo size={55} className="drop-shadow-lg" />
                <div>
                  <span className="text-xl font-black font-['Outfit'] text-white block">
                    Alluz
                  </span>
                  <span className="text-xs text-[#FFB414] font-semibold">
                    ENERGIA SOLAR
                  </span>
                </div>
              </div>
              <p className="text-white/50 text-sm leading-relaxed mb-5">
                Transformando a forma como o Brasil consome energia. 
                Sustentabilidade e tecnologia para um futuro mais brilhante.
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                {socialLinks.map(({ icon: Icon, href, label }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-[#FFB414]/10 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#FFB414] hover:to-[#FF8C00] transition-all duration-300 group border border-[#FFB414]/20"
                    aria-label={label}
                    data-testid={`social-${label.toLowerCase()}`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon className="w-5 h-5 text-[#FFB414] group-hover:text-white" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-bold text-base mb-5">Contato</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white/60 hover:text-[#FFB414] transition-colors text-sm">
                  <Mail className="w-4 h-4 text-[#FFB414]" />
                  <span>contato@alluzenergia.com.br</span>
                </li>
                <li className="flex items-center gap-3 text-white/60 hover:text-[#FFB414] transition-colors text-sm">
                  <Phone className="w-4 h-4 text-[#FFB414]" />
                  <span>(44) 99999-9999</span>
                </li>
                <li className="flex items-start gap-3 text-white/60 text-sm">
                  <MapPin className="w-4 h-4 text-[#FFB414] mt-0.5" />
                  <span>Maringá, PR - Brasil</span>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold text-base mb-5">Links Rápidos</h4>
              <ul className="space-y-2">
                {["Sobre", "Vantagens", "Missão", "Contato"].map((link) => (
                  <li key={link}>
                    <a 
                      href={`#${link.toLowerCase()}`}
                      className="text-white/60 hover:text-[#FFB414] transition-colors flex items-center gap-2 group text-sm"
                    >
                      <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Simulator CTA */}
            <div>
              <h4 className="text-white font-bold text-base mb-5">Simule Agora</h4>
              <p className="text-white/50 text-sm mb-5">
                Descubra quanto você pode economizar com energia solar.
              </p>
              <motion.a
                href="https://simulador.alluzenergia.com.br"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button className="btn-glow w-full text-white font-bold rounded-xl flex items-center justify-center gap-2 text-sm">
                  <AlluzLogo size={22} className="!filter-none !animate-none" />
                  Acessar Simulador
                </Button>
              </motion.a>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#FFB414]/20 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-5">
              <div className="text-white/40 text-xs text-center lg:text-left">
                <p>© {new Date().getFullYear()} Alluz Energia. Todos os direitos reservados.</p>
                <p className="mt-1">
                  Alluz Energia Sustentável e Tecnologia da Informação LTDA | 
                  CNPJ: 34.782.317/0001-49
                </p>
              </div>
              <div className="flex gap-5 text-white/40 text-xs">
                <a href="#" className="hover:text-[#FFB414] transition-colors">
                  Política de Privacidade
                </a>
                <a href="#" className="hover:text-[#FFB414] transition-colors">
                  Termos de Uso
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
